import React from 'react';
import { 
  Security, 
  Policy, 
  Payment, 
  LocalShipping, 
  AccountCircle, 
  Update, 
  SupportAgent,
  PrivacyTip,
  GppGood,
  VerifiedUser,
  Shield,
  Lock,
  Assignment,
  Description
} from '@mui/icons-material';

const PoliciesPageAR: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-5xl mx-auto p-4 sm:p-6 lg:p-8" dir="rtl">
        
        {/* Header Section */}
        <div className="mb-12 text-center">
          <div className="inline-flex items-center justify-center mb-6">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-primary to-primary-dark rounded-2xl blur-lg opacity-30"></div>
              <div className="relative bg-gradient-to-r from-primary to-primary-dark p-5 rounded-2xl shadow-xl">
                <Policy className="text-white text-4xl" />
              </div>
            </div>
          </div>
          
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            سياسات المنصة – بازار ماركت بليس
          </h1>
          
          <div className="h-1.5 w-32 bg-gradient-to-r from-primary to-primary-dark mx-auto rounded-full mb-4"></div>
          
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            آخر تحديث: {new Date().toLocaleDateString('ar-EG', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>
        </div>

        {/* Introduction Card */}
        <div className="mb-10 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border-r-4 border-primary shadow-sm">
          <div className="flex items-start gap-4">
            <Shield className="text-primary text-2xl mt-1 flex-shrink-0" />
            <div>
              <h3 className="font-bold text-xl text-gray-900 mb-3">أهلاً وسهلاً بكم في بازار</h3>
              <p className="text-gray-700 leading-relaxed text-lg">
                استخدامك لمنصتنا يُعد موافقة صريحة على السياسات التالية. 
                هذه السياسات توضح التزامات الأطراف المختلفة وحقوقهم وطرق تعاملنا مع البيانات والخدمات.
                نحن ملتزمون بتوفير تجربة آمنة وشفافة لجميع المستخدمين.
              </p>
            </div>
          </div>
        </div>

        {/* Policy Sections */}
        <div className="space-y-8">
          {[
            {
              icon: <PrivacyTip />,
              title: "١. سياسة الخصوصية وحماية البيانات",
              items: [
                "نقوم بجمع بيانات المستخدم فقط عند التسجيل أو إجراء عمليات شراء.",
                "نستخدم البيانات لتحسين تجربة المستخدم، وإدارة الطلبات، والتواصل الفعال.",
                "لا نقوم ببيع أو مشاركة البيانات مع أي طرف ثالث دون موافقة صريحة.",
                "جميع البيانات مخزنة بشكل آمن وتخضع للحماية بموجب قوانين حماية البيانات المصرية.",
                "للمستخدم الحق في طلب حذف بياناته الشخصية في أي وقت.",
                "نستخدم تشفير SSL لحماية البيانات أثناء نقلها عبر الإنترنت."
              ],
              color: "from-blue-500/20 to-blue-600/20",
              borderColor: "border-blue-500"
            },
            {
              icon: <Description />,
              title: "٢. سياسة المحتوى والإعلانات",
              items: [
                "يمنع نشر أي محتوى مخالف للقوانين أو يحتوي على معلومات مضللة أو احتيالية.",
                "يجب أن تكون المنتجات المعروضة حقيقية ومطابقة للوصف.",
                "يتم إزالة أي محتوى غير مناسب فورًا دون إشعار مسبق.",
                "يمنع استخدام صور غير مصرح بها أو منتهكة حقوق الملكية.",
                "يجب أن تكون الأسعار واضحة وتشمل جميع التكاليف الإضافية.",
                "يتم مراجعة المحتوى قبل النشر لضمان الجودة والملاءمة."
              ],
              color: "from-purple-500/20 to-purple-600/20",
              borderColor: "border-purple-500"
            },
            {
              icon: <Payment />,
              title: "٣. سياسة الدفع والاسترجاع",
              items: [
                "الدفع يتم عبر وسائل إلكترونية آمنة (بطاقات ائتمان، فوري، محافظ رقمية).",
                "يمكن للعميل طلب استرجاع المنتج خلال ١٤ يومًا من تاريخ الاستلام في حالة وجود عيب.",
                "تتم معالجة عمليات الإرجاع بواسطة خدمة العملاء لدينا، بالتنسيق مع الشركة البائعة.",
                "يتم إرجاع المبلغ خلال 5-7 أيام عمل من تأكيد الإرجاع.",
                "يجب أن يكون المنتج المرتجع في حالته الأصلية وبعبواته الأصلية.",
                "لا تشمل سياسة الإرجاع المنتجات التالفة بسبب سوء الاستخدام."
              ],
              color: "from-green-500/20 to-green-600/20",
              borderColor: "border-green-500"
            },
            {
              icon: <LocalShipping />,
              title: "٤. سياسة التوصيل والشحن",
              items: [
                "نستخدم شركات شحن موثوقة لضمان توصيل المنتجات في أسرع وقت.",
                "يمكن تتبع حالة الطلب مباشرة من خلال حساب العميل.",
                "وقت التوصيل يعتمد على موقع العميل وطبيعة المنتج.",
                "تكاليف الشحن تحدد حسب الوزن والمسافة وطريقة الشحن.",
                "يتم إعلام العميل بموعد التوصيل المتوقع عند تأكيد الطلب.",
                "للوحدات الإدارية والمناطق النائية قد تزيد مدة التوصيل 2-3 أيام إضافية."
              ],
              color: "from-orange-500/20 to-orange-600/20",
              borderColor: "border-orange-500"
            },
            {
              icon: <AccountCircle />,
              title: "٥. سياسة إدارة الحساب",
              items: [
                "للمستخدمين كامل الحرية في تعديل بياناتهم من خلال لوحة التحكم.",
                "لنا الحق في تعليق أو إلغاء الحساب في حال انتهاك السياسات.",
                "المستخدم مسؤول عن الحفاظ على سرية بيانات الدخول لحسابه.",
                "يجب أن تكون المعلومات الشخصية دقيقة ومحدثة.",
                "يمكن إغلاق الحساب بناءً على طلب المستخدم.",
                "يتم الاحتفاظ بسجلات الحساب لمدة 5 سنوات لأغراض قانونية وضريبية."
              ],
              color: "from-red-500/20 to-red-600/20",
              borderColor: "border-red-500"
            },
            {
              icon: <GppGood />,
              title: "٦. سياسة الأمان والحماية",
              items: [
                "نستخدم أنظمة حماية متقدمة ضد الاختراق والهجمات الإلكترونية.",
                "جميع المعاملات المالية مشفرة باستخدام تقنيات SSL/TLS.",
                "نقوم بعمل نسخ احتياطية دورية للبيانات.",
                "يتم تدريب فريقنا على التعامل مع الحوادث الأمنية.",
                "نبلغ المستخدمين فورًا في حالة حدوث أي خرق أمني.",
                "نلتزم بمعايير PCI DSS لأمان بيانات الدفع."
              ],
              color: "from-indigo-500/20 to-indigo-600/20",
              borderColor: "border-indigo-500"
            },
            {
              icon: <Update />,
              title: "٧. التحديثات والتعديلات",
              note: "نحتفظ بحق تعديل هذه السياسات في أي وقت. سيتم إعلام المستخدمين بالتغييرات الكبيرة عبر البريد الإلكتروني أو الإشعارات داخل المنصة. استمرار استخدام المنصة بعد التحديثات يعني الموافقة على السياسات الجديدة. نوصي بمراجعة هذه الصفحة دوريًا للاطلاع على آخر التحديثات."
            },
            {
              icon: <SupportAgent />,
              title: "٨. سياسة الدعم والتواصل",
              note: (
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
                    <span className="text-primary font-bold">البريد الإلكتروني:</span>
                    <a href="mailto:support@bazaar-marketplace.com" className="text-primary hover:underline font-medium">
                      support@bazaar-marketplace.com
                    </a>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
                    <span className="text-primary font-bold">واتساب:</span>
                    <a href="https://wa.me/201503323006" className="text-primary hover:underline font-medium">
                      +20 150 332 3006
                    </a>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
                    <span className="text-primary font-bold">ساعات العمل:</span>
                    <span className="text-gray-700">10 صباحًا - 10 مساءً (بتوقيت مصر)</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
                    <span className="text-primary font-bold">المطور:</span>
                    <a href="https://codeocean.tech" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium">
                      CODEOCEAN.TECH
                    </a>
                  </div>
                  <p className="text-gray-600 mt-2">
                    متوسط وقت الرد: خلال 4 ساعات عمل. نلتزم بحل المشكلات خلال 48 ساعة كحد أقصى.
                  </p>
                </div>
              )
            }
          ].map((section, index) => (
            <section 
              key={index} 
              className="bg-white rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden border border-gray-100"
            >
              <div className="p-6">
                <div className="flex items-start gap-4 mb-6">
                  <div className={`p-4 rounded-xl bg-gradient-to-r ${section.color}`}>
                    <span className="text-gray-800 text-2xl">{section.icon}</span>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                      {section.title}
                      <span className={`h-1 flex-1 bg-gradient-to-r ${section.borderColor} to-transparent rounded-full`}></span>
                    </h2>
                    
                    {section.items && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                        {section.items.map((item, itemIndex) => (
                          <div key={itemIndex} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                            <VerifiedUser className="text-primary text-sm mt-1 flex-shrink-0" />
                            <span className="text-gray-700 leading-relaxed">{item}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {section.note && (
                      <div className="mt-4 p-5 bg-gradient-to-r from-gray-50 to-white rounded-xl border-r-4 border-primary">
                        {typeof section.note === 'string' ? (
                          <p className="text-gray-700 leading-relaxed text-lg">{section.note}</p>
                        ) : (
                          section.note
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {section.borderColor && (
                <div className={`h-1 w-full bg-gradient-to-r ${section.borderColor} to-transparent`}></div>
              )}
            </section>
          ))}
        </div>

        {/* Important Notice */}
        <div className="mt-12 p-6 bg-gradient-to-r from-gray-900 to-black rounded-2xl text-white">
          <div className="flex items-center gap-4 mb-4">
            <Lock className="text-white text-2xl" />
            <h3 className="text-xl font-bold">ملاحظات هامة</h3>
          </div>
          <div className="space-y-3">
            <p className="text-gray-200">
              ⚠️ هذه السياسات جزء لا يتجزأ من شروط الاستخدام وتعتبر ملزمة قانونيًا.
            </p>
            <p className="text-gray-200">
              📝 نوصي بحفظ نسخة من هذه السياسات أو طباعتها للرجوع إليها عند الحاجة.
            </p>
            <p className="text-gray-200">
              🔒 انتهاك أي من هذه السياسات قد يؤدي إلى تعليق الحساب أو اتخاذ إجراءات قانونية.
            </p>
            <p className="text-gray-200">
              📞 للاستفسارات القانونية، يرجى التواصل مع فريق الدعم للحصول على المساعدة.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-gray-200 text-center">
          <div className="mb-6">
            <div className="inline-flex flex-wrap items-center justify-center gap-3 bg-gradient-to-r from-primary/10 to-primary/20 px-6 py-4 rounded-2xl">
              <span className="text-primary font-bold text-lg">بازار ماركت بليس</span>
              <span className="text-gray-500">•</span>
              <span className="text-gray-600">رقم ضريبي: 752-382-462</span>
              <span className="text-gray-500">•</span>
              <span className="text-gray-600">سجل تجاري: 123456</span>
            </div>
          </div>
          
          <div className="text-gray-600 text-sm leading-relaxed max-w-2xl mx-auto space-y-2">
            <p>
              شكراً لاستخدامك بازار ماركت بليس – السوق الرقمي الذي يعكس تطلعات المستخدم المصري.
            </p>
            <p className="text-gray-500">
              جميع الحقوق محفوظة © {new Date().getFullYear()} بازار ماركت بليس. جميع السياسات خاضعة للقوانين واللوائح المصرية.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <button
              onClick={() => window.print()}
              className="px-6 py-2 bg-white border border-primary text-primary rounded-lg hover:bg-primary hover:text-white transition-all duration-300"
            >
              طباعة السياسات
            </button>
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="px-6 py-2 bg-gradient-to-r from-primary to-primary-dark text-white rounded-lg hover:opacity-90 transition-opacity duration-300"
            >
              العودة للأعلى
            </button>
            <a
              href="/terms-conditions"
              className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-300"
            >
              شروط الاستخدام
            </a>
          </div>
        </div>

        {/* Quick Links */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <a href="/privacy-policy" className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-sm">
            سياسة الخصوصية
          </a>
          <a href="/refund-policy" className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-sm">
            سياسة الاسترجاع
          </a>
          <a href="/shipping-policy" className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-sm">
            سياسة الشحن
          </a>
          <a href="/faq" className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-sm">
            الأسئلة الشائعة
          </a>
        </div>
      </div>
    </div>
  );
};

export default PoliciesPageAR;